import type { GeoLocation } from '../Store'
import { Venue } from './venueData'

interface GeoPoint {
  lat: number
  lon: number
}

const EARTH_RADIUS_METERS = 6371000

/**
 * Calculates the distance between two GPS coordinates using the Haversine formula.
 * @param p1 The first point.
 * @param p2 The second point.
 * @returns The distance in meters.
 */
export function getPointDistance(p1: GeoPoint, p2: GeoPoint): number {
  const dLat = (p2.lat - p1.lat) * (Math.PI / 180)
  const dLon = (p2.lon - p1.lon) * (Math.PI / 180)
  const lat1 = p1.lat * (Math.PI / 180)
  const lat2 = p2.lat * (Math.PI / 180)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return EARTH_RADIUS_METERS * c
}

export interface ClosestVenue extends Venue {
  inside: boolean // true if the coordinates are matched to within the agency radius
  distance: number // distance from user position in meters
}

/**
 * Finds closest venue to a given position
 * @param position The user's location to check.
 * @param venueList The array of TAB venues with locations.
 */
export const findClosestVenue = (position: GeoLocation, venueList: Venue[]): ClosestVenue => {
  const userPosition: GeoPoint = { lat: Number(position.latitude), lon: Number(position.longitude) }

  let closestVenue: Venue = venueList[0]
  let minDistance: number = getPointDistance(userPosition, {
    lat: closestVenue.latitude,
    lon: closestVenue.longitude,
  })

  for (let i = 1; i < venueList.length; i++) {
    const venue = venueList[i]

    // ignore venues without a valid radius
    if (venue.radius < 0) continue

    const distance = getPointDistance(userPosition, { lat: venue.latitude, lon: venue.longitude })
    if (distance < minDistance) {
      closestVenue = venue
      minDistance = distance
    }
  }

  return {
    ...closestVenue,
    distance: Math.round(minDistance),
    inside: minDistance < closestVenue.radius,
  }
}

export const prettifyDistance = (dist?: number | null) => {
  if (dist == undefined || dist == 0) return '0m'
  if (dist < 1000) return `${Math.round(dist) != dist ? dist.toFixed(3) : dist.toFixed(1)}m`
  const kilometres = dist / 1000
  if (kilometres < 10) return `${kilometres.toFixed(2)}km`
  return `${Math.round(kilometres).toLocaleString('en-AU')}km`
}
